import React, { useCallback, useEffect, useState } from "react";
import {
  ANIMATION_CONFIG,
  HINT_CONFIG,
  LEVEL_CONFIG,
  getRandomAnimal,
  playButtonSound,
  playCompleteSound,
  playCorrectSound,
  playWrongSound,
} from "../../constants";
import { useGame } from "../../context";
import "./MultiplesGame.css";

// 倍數類型
const MULTIPLE_TYPES = [2, 5, 10];

// 生成問題
const generateQuestion = (level) => {
  const config =
    LEVEL_CONFIG.MULTIPLES_CONFIG[Math.min(level, 10)] ||
    LEVEL_CONFIG.MULTIPLES_CONFIG[10];
  const sequenceLength = config.length;

  const animal = getRandomAnimal();

  // 隨機選擇倍數類型
  const multipleType =
    MULTIPLE_TYPES[Math.floor(Math.random() * MULTIPLE_TYPES.length)];

  // 根據倍數類型決定起始數字範圍
  let maxStart;
  if (multipleType === 2) {
    maxStart = Math.floor((100 - (sequenceLength - 1) * 2) / 2);
  } else if (multipleType === 5) {
    maxStart = Math.floor((100 - (sequenceLength - 1) * 5) / 5);
  } else {
    maxStart = Math.floor((100 - (sequenceLength - 1) * 10) / 10);
  }
  maxStart = Math.max(1, Math.min(maxStart, 10));

  const startNumber = Math.floor(Math.random() * maxStart) + 1;

  // 生成完整序列
  const sequence = Array(sequenceLength)
    .fill(null)
    .map((_, index) => startNumber * multipleType + index * multipleType);

  // 確保所有數字在100以內
  const validSequence = sequence.filter((n) => n <= 100);

  // 隨機選擇缺失位置（排除首尾）
  const missingIndex =
    Math.floor(Math.random() * (validSequence.length - 2)) + 1;
  const answer = validSequence[missingIndex];

  // 創建顯示序列
  const displaySequence = validSequence.map((num, index) => ({
    value: num,
    isHidden: index === missingIndex,
  }));

  return {
    sequence: validSequence,
    displaySequence,
    missingIndex,
    answer,
    multipleType,
    animal,
    question: `${animal.name}在數${multipleType}的倍數，找出缺少的數字！`,
  };
};

// 數軸組件
const NumberLine = ({ sequence, missingIndex, multipleType, animal }) => {
  const min = Math.min(...sequence) - multipleType;
  const max = Math.max(...sequence) + multipleType;

  return (
    <div className="number-line-container">
      <div className="number-line">
        <div className="line-base"></div>
        {sequence.map((num, index) => {
          const position = ((num - min) / (max - min)) * 100;
          const isHidden = index === missingIndex;

          return (
            <div
              key={index}
              className={`number-point ${isHidden ? "hidden-point" : ""}`}
              style={{ left: `${position}%` }}
            >
              <div className="point-marker">
                {isHidden ? "❓" : animal.emoji}
              </div>
              <div className={`point-value ${isHidden ? "hidden-value" : ""}`}>
                {isHidden ? "?" : num}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// 序列顯示組件
const SequenceDisplay = ({ displaySequence, userAnswer, showResult }) => {
  return (
    <div className="sequence-display">
      {displaySequence.map((item, index) => (
        <React.Fragment key={index}>
          <div
            className={`sequence-item ${item.isHidden ? "hidden-item" : ""} ${
              item.isHidden && showResult ? showResult : ""
            }`}
          >
            {item.isHidden ? userAnswer || "?" : item.value}
          </div>
          {index < displaySequence.length - 1 && (
            <span className="sequence-arrow">→</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

// 倍數規律提示
const MultipleHint = ({ multipleType, sequence, missingIndex }) => {
  const prev = sequence[missingIndex - 1];
  const next = sequence[missingIndex + 1];

  return (
    <div className="multiple-hint-content">
      <div className="hint-rule">
        <span className="rule-title">📐 規律：</span>
        <span className="rule-text">
          這是 <strong>{multipleType}</strong> 的倍數序列
        </span>
      </div>
      <div className="hint-pattern">
        <span className="pattern-item">{prev}</span>
        <span className="pattern-arrow">+{multipleType}</span>
        <span className="pattern-item missing">?</span>
        <span className="pattern-arrow">+{multipleType}</span>
        <span className="pattern-item">{next}</span>
      </div>
      <div className="hint-calculation">
        <p>
          {prev} + {multipleType} = <strong>{prev + multipleType}</strong>
        </p>
        <p>或者</p>
        <p>
          {next} - {multipleType} = <strong>{next - multipleType}</strong>
        </p>
      </div>
    </div>
  );
};

// 生成3個相似選項
const generateChoices = (correctAnswer, multipleType) => {
  const choices = new Set([correctAnswer]);

  // 生成相似的錯誤選項（相鄰的倍數）
  const generateSimilar = () => {
    const offsets = [
      -multipleType,
      multipleType,
      -multipleType * 2,
      multipleType * 2,
    ];
    for (const offset of offsets) {
      const similar = correctAnswer + offset;
      if (similar > 0 && similar <= 100 && !choices.has(similar)) {
        choices.add(similar);
        if (choices.size >= 3) break;
      }
    }

    // 如果還不夠，添加隨機倍數
    while (choices.size < 3) {
      const randomMultiple =
        (Math.floor(Math.random() * 10) + 1) * multipleType;
      if (
        randomMultiple > 0 &&
        randomMultiple <= 100 &&
        !choices.has(randomMultiple)
      ) {
        choices.add(randomMultiple);
      }
    }
  };

  generateSimilar();

  // 轉換為數組並隨機排序
  return Array.from(choices).sort(() => Math.random() - 0.5);
};

const MultiplesGame = ({ onBack }) => {
  const { state, actions } = useGame();
  const [question, setQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState("");
  const [showResult, setShowResult] = useState(null);
  const [showHint, setShowHint] = useState(false);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [choices, setChoices] = useState([]);

  const currentLevel = state.gameProgress.multiples.level;
  const gameProgress = state.gameProgress.multiples;

  // 初始化遊戲
  useEffect(() => {
    actions.startGame("multiples");
    generateNewQuestion();

    return () => {
      actions.endGame();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 等級提升音效
  useEffect(() => {
    if (state.showLevelUp) {
      playCompleteSound();
    }
  }, [state.showLevelUp]);

  // 生成新問題
  const generateNewQuestion = useCallback(() => {
    const newQuestion = generateQuestion(currentLevel);
    setQuestion(newQuestion);
    actions.setQuestion(newQuestion);
    setUserAnswer("");
    setShowResult(null);
    setShowHint(false);
    setHintsUsed(0);

    // 生成3個選項
    setChoices(generateChoices(newQuestion.answer, newQuestion.multipleType));
  }, [currentLevel, actions]);

  // 處理選擇答案
  const handleChoiceSelect = (choice) => {
    if (showResult) return; // 防止重複選擇
    playButtonSound();
    setUserAnswer(choice.toString());
    checkAnswer(choice);
  };

  // 檢查答案
  const checkAnswer = (numAnswer) => {
    if (numAnswer === question.answer) {
      playCorrectSound();
      setShowResult("correct");
      actions.answerCorrect("multiples", hintsUsed > 0);

      setTimeout(() => {
        generateNewQuestion();
      }, ANIMATION_CONFIG.SUCCESS_DELAY);
    } else {
      playWrongSound();
      setShowResult("wrong");
      actions.answerWrong("multiples");

      setTimeout(() => {
        setShowResult(null);
        setUserAnswer("");
      }, ANIMATION_CONFIG.WRONG_SHAKE_DURATION);
    }
  };

  // 處理答案提交（保留用於表單提交）
  // const handleSubmit = (e) => {
  //   e.preventDefault();
  //   if (!userAnswer.trim()) return;
  //   const numAnswer = parseInt(userAnswer, 10);
  //   if (isNaN(numAnswer)) {
  //     actions.showFeedback("請輸入數字！", "warning");
  //     return;
  //   }
  //   checkAnswer(numAnswer);
  // };

  // 處理提示
  const handleHint = () => {
    playButtonSound();
    if (hintsUsed >= HINT_CONFIG.MAX_HINTS_PER_QUESTION) {
      actions.showFeedback("這題已經沒有更多提示了！", "info");
      return;
    }

    setShowHint(true);
    setHintsUsed((prev) => prev + 1);
    actions.useHint();
  };

  if (!question) {
    return (
      <div className="game-loading">
        <span className="loading-emoji">🔄</span>
        <span>載入中...</span>
      </div>
    );
  }

  return (
    <div
      className="multiples-game"
      style={{ backgroundColor: question.animal.bgColor }}
    >
      {/* 遊戲頭部 */}
      <div className="game-header">
        <button className="back-button" onClick={onBack}>
          <span>←</span> 返回
        </button>

        <div className="game-info">
          <div className="info-item">
            <span className="info-icon">🎯</span>
            <span className="info-value">等級 {currentLevel}</span>
          </div>
          <div className="info-item">
            <span className="info-icon">⭐</span>
            <span className="info-value">{state.totalStars}</span>
          </div>
          <div className="info-item">
            <span className="info-icon">🏆</span>
            <span className="info-value">{state.totalScore}</span>
          </div>
        </div>
      </div>

      {/* 進度條 */}
      <div className="level-progress">
        <div className="progress-label">
          升級進度：{gameProgress.correctCount % 5} / 5
        </div>
        <div className="progress-bar">
          <div
            className="progress-fill"
            style={{ width: `${(gameProgress.correctCount % 5) * 20}%` }}
          />
        </div>
      </div>

      {/* 主遊戲區域 */}
      <div className="game-main">
        {/* 問題區域 */}
        <div className="question-section flex flex-row justify-center">
          <div className="animal-display">
            <span className="animal-emoji large">{question.animal.emoji}</span>
            <div className="animal-speech">{question.question}</div>
          </div>

          <div className="multiple-badge">
            <span className="badge-label">倍數類型</span>
            <span className="badge-value">×{question.multipleType}</span>
          </div>
        </div>

        {/* 數軸視覺化 */}
        <NumberLine
          sequence={question.sequence}
          missingIndex={question.missingIndex}
          multipleType={question.multipleType}
          animal={question.animal}
        />

        {/* 序列顯示 */}
        <SequenceDisplay
          displaySequence={question.displaySequence}
          userAnswer={userAnswer}
          showResult={showResult}
        />

        {/* 選擇答案區 */}
        <div className="answer-section">
          <div className="choice-buttons">
            {choices.map((choice, index) => (
              <button
                key={index}
                type="button"
                className={`choice-button ${
                  userAnswer === choice.toString() ? showResult || "" : ""
                }`}
                onClick={() => handleChoiceSelect(choice)}
                disabled={showResult === "correct"}
              >
                {choice}
              </button>
            ))}
          </div>

          <div className="action-buttons">
            <button
              type="button"
              className="hint-button"
              onClick={handleHint}
              disabled={hintsUsed >= HINT_CONFIG.MAX_HINTS_PER_QUESTION}
            >
              💡 提示 ({HINT_CONFIG.MAX_HINTS_PER_QUESTION - hintsUsed}次)
            </button>
          </div>
        </div>

        {/* 結果反饋 */}
        {showResult === "correct" && (
          <div className="result-overlay correct">
            <div className="result-content">
              <span className="result-emoji">🎉</span>
              <span className="result-text">答對了！</span>
              <span className="result-detail">
                {question.multipleType} 的倍數規律真厲害！
              </span>
            </div>
          </div>
        )}

        {showResult === "wrong" && (
          <div className="result-message wrong">
            <span>再試試看！觀察數字之間的規律 💪</span>
          </div>
        )}
      </div>

      {/* 提示彈窗 */}
      {showHint && (
        <div className="hint-overlay" onClick={() => setShowHint(false)}>
          <div className="hint-card" onClick={(e) => e.stopPropagation()}>
            <div className="hint-header">
              <span className="hint-icon">💡</span>
              <span className="hint-title">倍數規律提示</span>
            </div>
            <div className="hint-content">
              <MultipleHint
                multipleType={question.multipleType}
                sequence={question.sequence}
                missingIndex={question.missingIndex}
              />
            </div>
            <button
              className="hint-close-btn"
              onClick={() => setShowHint(false)}
            >
              我知道了 ✨
            </button>
          </div>
        </div>
      )}

      {/* 升級動畫 */}
      {state.showLevelUp && (
        <div className="level-up-overlay">
          <div className="level-up-content">
            <span className="level-up-emoji">🎊</span>
            <span className="level-up-text">等級提升！</span>
            <span className="level-up-level">Level {currentLevel}</span>
            <button onClick={actions.hideLevelUp} className="level-up-btn">
              太棒了！
            </button>
          </div>
        </div>
      )}

      {/* 連續答對提示 */}
      {state.streak >= 3 && (
        <div className="streak-indicator">🔥 連續答對 {state.streak} 題！</div>
      )}

      {/* 倍數小提示 */}
      <div className="multiples-tip">
        <span className="tip-icon">💡</span>
        <span className="tip-text">
          {question.multipleType === 2 && "2的倍數都是偶數哦！"}
          {question.multipleType === 5 && "5的倍數結尾都是0或5！"}
          {question.multipleType === 10 && "10的倍數結尾都是0！"}
        </span>
      </div>
    </div>
  );
};

export default MultiplesGame;
